import { LocaleCode } from '@choco-admin/types';
import { CountryCode } from '@choco-admin/utils';
import { Divider } from '@heroui/react';
import React from 'react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';

import {
  Input as FormInput,
  PhoneInput,
  LocaleSelectField,
} from '../../../../../components/FormComponents';
import { UserFormData } from '../hooks/useUserFormValidation';

interface BasicInformationSectionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  countryCode: CountryCode;
  onChangeCountryCode: (newCountryCode: CountryCode) => void;
  setValue: UseFormSetValue<UserFormData>;
  isLoginEmailMethodSelected: boolean;
  isLoginPhoneMethodSelected: boolean;
  getInputSubLabel: (isRequired: boolean) => string;
  disabled?: boolean;
}

export const BasicInformationSection: React.FC<
  BasicInformationSectionProps
> = ({
  control,
  errors,
  countryCode,
  onChangeCountryCode,
  setValue,
  isLoginEmailMethodSelected,
  isLoginPhoneMethodSelected,
  getInputSubLabel,
  disabled = false,
}) => {
  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">
          Basic Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <FormInput
                id="user-name"
                name={field.name}
                label="Full Name"
                value={field.value}
                placeholder="Enter full name"
                required={true}
                isInvalid={!!errors.name}
                errorMessage={errors.name?.message}
                onChange={field.onChange}
                disabled={disabled}
              />
            )}
          />

          {/* Locale */}
          <Controller
            name="locale"
            control={control}
            render={({ field }) => (
              <LocaleSelectField
                value={field.value as LocaleCode}
                onChange={field.onChange}
                isRequired={true}
                errorMessage={errors.locale?.message}
                data-testid="user-locale-field"
                aria-label="User locale"
                isDisabled={disabled}
              />
            )}
          />

          {/* Phone */}
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                id="user-phone"
                name={field.name}
                label="Phone"
                value={field.value || ''}
                required={isLoginPhoneMethodSelected}
                isInvalid={!!errors.phone}
                errorMessage={errors.phone?.message}
                countryCode={countryCode}
                onChangeCountryCode={(newCountryCode) => {
                  onChangeCountryCode(newCountryCode);
                  setValue('countryCode', newCountryCode);
                }}
                onChange={(phoneNumber) => {
                  field.onChange(phoneNumber);
                }}
                placeholder="Enter phone number"
                helperText={getInputSubLabel(isLoginPhoneMethodSelected)}
                data-testid="user-phone-input"
                disabled={disabled}
              />
            )}
          />

          {/* Email */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <FormInput
                id="user-email"
                name={field.name}
                label={
                  isLoginEmailMethodSelected
                    ? 'Email Address'
                    : 'Contact Email Address'
                }
                value={field.value || ''}
                placeholder="Enter email address"
                type="email"
                required={isLoginEmailMethodSelected}
                isInvalid={!!errors.email}
                errorMessage={errors.email?.message}
                onChange={field.onChange}
                disabled={disabled}
                helperText={getInputSubLabel(isLoginEmailMethodSelected)}
                helperPlacement="above"
              />
            )}
          />
        </div>
      </div>

      <Divider />
    </>
  );
};
